/* eslint-disable @typescript-eslint/no-duplicate-enum-values */
/* eslint-disable no-undefined */
//
// Chronon - A date/time library for Nova
//
// A chronon is a hypothetical quantum of time: https://en.wikipedia.org/wiki/Chronon
//
//
// Design Goals:
//    * Pure functions - no mutations of inputs, no side-effects
//    * Enforce explicit timezone handling when possible
//
import { DateTime } from 'luxon';

import type { ITimeInterval } from './interval';
import { isDate, isDefined, isString } from 'class-validator';
import { DateLike, NovaDate } from './nova-date';
import type { Maybe } from './globals';

/**
 * Enum for DateTime Formats
 * https://github.com/moment/luxon/blob/master/docs/formatting.md
 * @readonly
 * @enum { String }
 */
export enum LuxonDateTimeFormats {
  /** AM, PM **/
  AMPM = 'a',

  /** Jan 1, 2024 **/
  ShortMonthDayYear = 'DD',

  /** 2024-02-01T14:30:00 */
  ISO = "yyyy-MM-dd'T'HH:mm:ss",

  /** 01012022 **/
  MonthDayYear = 'MMddyyyy',

  /** 01/01/2022 */
  MonthDayYearSlashed = 'MM/dd/yyyy',

  /** 01/01/2022 @ 5:30 PM */
  MonthDayYearSlashedTimeAMPM = 'D @ t',

  /** 01/01/22 @ 5:30PM */
  MonthDayYearSlashedTimeAMPMCompact = 'MM/dd/yy @ hh:mma',

  /** 01/01/2022 @ 17:30 */
  MonthDayYearSlashedTime24 = 'D @ T',

  /** 01/01/2022-0530 */
  MonthDayYearSlashedTimeNoSpace = 'MM/dd/yyyy-Hmm',

  /** 5:30 AM */
  Extended12HrTimeAMPM = 't',

  /** 05:30 */
  Extended12HrTimePadding = 'hh:mm',

  /** Aug 6, 2014 */
  DateWithAbbreviatedMonth = 'DD',

  /** 5:30 AM - EST */
  Extended12HrTimeAMPMWithAbbreviatedNamedOffset = 't - ZZZZ',

  /** 17:30 - PDT */
  Extended24HrTimeWithAbbreviatedNamedOffset = 'T - ZZZZ',

  /** 16:30:15 | 06:30:20 */
  Extended24HrTimeLeadingZeroHourSeconds = 'TT',

  /** 10 PM */
  Hour12 = 'h a',

  /** 22 */
  Hour24 = 'H',

  /** 17:30 */
  Extended24HrTime = 'T',

  /** 5:30 */
  Extended24HrTimeNoPadding = 'H:mm',

  /** Thursday, Sep 1, 2022 @ 6:00 PM */
  LongDateTimeShortMonth = 'EEEE, DD @ t',

  /** Thursday, Sep 1, 2022 @ 18:00 (EST) */
  LongDateTime24ShortMonthWithAbbreviatedNamedOffset = 'EEEE, DD @ T (ZZZZ)',

  /** Thursday, Sep 1, 2022 @ 6:00 PM (MDT) */
  LongDateTimeShortMonthWithAbbreviatedNamedOffset = 'EEEE, DD @ t (ZZZZ)',

  /** Thu, Sep 1, 2022 @ 18:00 (EST) */
  LongDateTime24ShortDayShortMonthWithAbbreviatedNamedOffset = 'EEE, DD @ T (ZZZZ)',

  /** Thu, Sep 1, 2022 @ 6:00 PM (MDT) */
  LongDateTimeShortDayShortMonthWithAbbreviatedNamedOffset = 'EEE, DD @ t (ZZZZ)',

  /** Thursday, Sep 1, 2022 @ 18:00 */
  LongDateTime24ShortMonth = 'EEEE, DD @ T',

  LongDateLongMonth = 'EEEE, MMMM d, yyyy',

  /** Thu, May 18, 2023 */
  LongDateShortMonth = 'EEE, DD',

  /** Dec 7 @ 10:30 AM */
  ShortMonthDayTimeAMPM = 'MMM d @ t',

  /** Dec 7 @ 17:30 */
  ShortMonthDayTime24 = 'MMM d @ T',

  /** Dec 7, 2022 @ 10:30 AM */
  ShortMonthYearDayTimeAMPM = 'DD @ t',

  /** Dec 7, 2022 @ 17:30 */
  ShortMonthYearDayTime24 = 'DD @ T',

  /** America/New_York */
  IANANamedOffset = 'z',

  /** EST **/
  AbbreviatedNamedOffset = 'ZZZZ',

  /** Eastern Standard Time **/
  UnabbreviatedNamedOffset = 'ZZZZZ',

  /** Mon **/
  ShortWeekday = 'EEE',

  // New v2 Formats
  /** 2022-01-01 */
  DateDashed = 'yyyy-MM-dd',

  /** Mon, 12/04/2023 - 08:00 AM (EST) */
  ShortDayDateSlashed12HourZone = 'EEE, D - hh:mm a (ZZZZ)',

  /** Mon, 12/04/2023 - 18:00 (EST) */
  ShortDayDateSlashed24HourZone = 'EEE, D - T (ZZZZ)',

  /** Mon, 12/04/2023 - 08:00 AM */
  ShortDayDateSlashed12Hour = 'EEE, D - hh:mm a',

  /** Mon, 12/04/2023 - 08:00 */
  ShortDayDateSlashed24Hour = 'EEE, D - HH:mm',

  /** Mon, 12/04/2023 */
  ShortDayDateSlashed = 'EEE, D',

  /** 05:30 AM */
  Extended12HrTimePaddingAMPM = 'hh:mm a',

  /** 3/20/2024 @ 03:30 */
  DateSlashedTimestamp24HourTime = 'D @ HH:mm',

  /** 3/20/2024 @ 03:30 PM */
  DateSlashedTimestamp12HourTimeAMPM = 'D @ hh:mm a',

  /** 3/20/2024 - 03:30 */
  DateSlashedDashTimestamp24HourTime = 'D - HH:mm',

  /** 3/20/2024 - 03:30 PM */
  DateSlashedDashTimestamp12HourTimeAMPM = 'D - hh:mm a'
}

export enum LuxonLocales {
  /** Jan 31, 2025 */
  enUS = 'en-US'
}

export enum Weekday {
  Sunday = 'Sunday',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday'
}

export function addDays(inputDate: Date, days: number) {
  const date = new Date(inputDate);
  date.setDate(date.getDate() + days);
  return date;
}

export function addWeeks(args: { utcDate: Date; numberOfWeeks: number; timezone: string }): Date {
  const { utcDate, numberOfWeeks, timezone } = args;
  return toDateTime(utcDate, { zone: timezone }).plus({ weeks: numberOfWeeks }).toJSDate();
}

const weekdayToNumber: Record<string, number> = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6
};

export function nextTimezoneWeekday(args: { weekday: Weekday; utcDate: Date; timezone: string }) {
  const { weekday, utcDate, timezone } = args;
  const utcDateInTimezone = toDateTime(utcDate, { zone: timezone });

  const targetDay = weekdayToNumber[weekday];
  const currentDay = utcDateInTimezone.weekday % 7;

  let daysToAdd = targetDay - currentDay;
  if (daysToAdd <= 0) {
    daysToAdd += 7;
  }

  return utcDateInTimezone.plus({ days: daysToAdd }).toJSDate();
}

// This next function is used to replicate an appt/reserve interval to a next weekday
// It uses nextTimezoneWeekday to get the next weekday and apply the interval start and end
// But it also checks if the end time has a date offset to apply
// if the interval starts at a day and it ends in the next, the nextTimezoneWeekday would
// apply the start and end time to the same date
export function nextTimezoneWeekdayInterval(args: {
  weekday: Weekday;
  utcStartDate: Date;
  utcEndDate: Date;
  timezone: string;
}): ITimeInterval {
  const { weekday, utcStartDate, utcEndDate, timezone } = args;
  const start = nextTimezoneWeekday({
    weekday,
    utcDate: utcStartDate,
    timezone
  });

  const daysOffset =
    DateTime.fromJSDate(utcEndDate).setZone(timezone).day -
    DateTime.fromJSDate(utcStartDate).setZone(timezone).day;

  const end = DateTime.fromJSDate(
    nextTimezoneWeekday({
      weekday,
      utcDate: utcEndDate,
      timezone
    })
  )
    .plus({
      days: daysOffset
    })
    .toJSDate();

  return {
    start,
    end
  };
}

export function getSupportedTimezones(): string[] {
  return (Intl as any).supportedValuesOf('timeZone') as string[];
}

export function roundDownToNearestMinute(date: Date): Date {
  const dateTime = DateTime.fromJSDate(date);
  const startOfMinute = dateTime.startOf('minute');
  return startOfMinute.toJSDate();
}

export function minDate(date1: Date, date2: Date): Date {
  return date1 < date2 ? date1 : date2;
}

export function maxDate(date1: Date, date2: Date): Date {
  return date1 > date2 ? date1 : date2;
}

export function absDiffDatesInMillis(date1: Date, date2: Date): number {
  const start = DateTime.fromJSDate(date1);
  const end = DateTime.fromJSDate(date2);

  return Math.abs(end.diff(start, 'millisecond').toMillis());
}

export function formatDateTimeWithMilitarySupport(
  dateTime: string | Date,
  timezone: Maybe<string>,
  twelveHoursFormat: LuxonDateTimeFormats,
  formatAsMilitary: boolean,
  twentyFourHoursFormat: LuxonDateTimeFormats,
  locale = 'en'
) {
  if (isDate(dateTime)) {
    dateTime = (dateTime as Date).toISOString();
  }
  // See supported ISO formats: https://moment.github.io/luxon/#/parsing?id=iso-8601
  const luxonDate = DateTime.fromISO(dateTime, {
    setZone: timezone === null, // Preserve timezone informed in the string, if none explicitly provided.
    zone: timezone ?? undefined
  });
  return luxonDate.toFormat(formatAsMilitary ? twentyFourHoursFormat : twelveHoursFormat, {
    locale
  });
}

/*
 * Formats a duration in minutes to a human-readable string, negative durations are converted to positive
 * invalid durations are returned as an empty string
 * @param minutes - The duration in minutes
 * @returns A human-readable string representing the duration
 */
export function formatDuration(minutes: number) {
  if (isString(minutes)) {
    minutes = Number(minutes);
  }

  if (isNaN(minutes) || minutes === 0) {
    return minutes === 0 ? '0 minutes' : '';
  }

  minutes = Math.abs(minutes);
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);

  const hoursText = hours > 0 ? `${hours} hour${hours > 1 ? 's' : ''}` : '';
  const minsText = mins > 0 ? `${mins} minute${mins > 1 ? 's' : ''}` : '';

  return [hoursText, minsText].filter(Boolean).join(' and ');
}

/**
 * Normalizes any DateLike input (string, Date, DateTime, NovaDate) into a Luxon DateTime.
 * Handles the Object-vs-String challenge: even if a param is typed as Date,
 * the runtime value may be a string from API responses.
 *
 * @param input - A DateLike value (string | Date | DateTime | NovaDate)
 * @param opts  - Optional zone to set on the resulting DateTime
 */
export function toDateTime(input: DateLike, opts?: { zone?: string }): DateTime {
  if (DateTime.isDateTime(input)) {
    return opts?.zone ? input.setZone(opts.zone) : input;
  }

  if (input instanceof NovaDate) {
    const dt = input.toDateTime();
    return opts?.zone ? dt.setZone(opts.zone) : dt;
  }

  if (isDate(input)) {
    const dt = DateTime.fromJSDate(input as Date);
    return opts?.zone ? dt.setZone(opts.zone) : dt;
  }

  if (isString(input)) {
    const isoOpts = opts?.zone ? { zone: opts.zone } : { setZone: true };
    const dtISO = DateTime.fromISO(input as string, isoOpts);
    if (dtISO.isValid) {
      return dtISO;
    }

    const dtRfc = DateTime.fromRFC2822(input as string);
    if (dtRfc.isValid) {
      return opts?.zone ? dtRfc.setZone(opts.zone) : dtRfc;
    }

    const dtSql = DateTime.fromSQL(input as string);
    if (dtSql.isValid) {
      return opts?.zone ? dtSql.setZone(opts.zone) : dtSql;
    }
  }

  throw new Error(`toDateTime: unable to parse date input: ${String(input)}`);
}

/**
 * Like toDateTime, but returns DateTime.now() when the input is null, undefined, or empty string.
 * Mirrors moment.tz(value, zone) behavior where missing values silently become "now".
 */
export function toDateTimeOrNow(
  input: DateLike | null | undefined,
  opts?: { zone?: string }
): DateTime {
  if (!isDefined(input) || input === '') {
    return opts?.zone ? DateTime.now().setZone(opts.zone) : DateTime.now();
  }
  return toDateTime(input, opts);
}

/**
 * Validates whether the given input is a valid date. Replaces the moment-based isValidDate.
 * Accepts Date objects and ISO 8601 strings.
 */
export function isValidDateInput(date: unknown): boolean {
  if (date && Object.prototype.toString.call(date) === '[object Date]' && !isNaN(date as number)) {
    return true;
  }
  if (typeof date === 'string') {
    return DateTime.fromISO(date).isValid;
  }
  return false;
}
